import PropTypes from "prop-types";
import React, { memo, useEffect, useReducer, useRef } from "react";
import { useSelector } from "react-redux";
import Link from "../../../components/LinkWithCountry/LinkWithCountry";
import ReCAPTCHAForm from "../../../components/ReCaptcha/ReCaptcha";
import { orderTypeIds } from "../../../shared/constants/order";
import { storeTypes } from "../../../shared/constants/store";
import * as phoneService from "../../../shared/utilities/phone";
import styles from "./PickupLocationSearch.module.css";
import useAddressSearch from "./hooks/useAddressSearch";
import useSavedLocations from "./hooks/useSavedLocations";
import useSearchStores from "./hooks/useSearchStores";
import useUserAddress from "./hooks/useUserAddress";

const searchIcon = require("../../../components/DeliveryPickUpWidget/imgs/search-icon.png");
const locationIcon = require("../../../components/DeliveryPickUpWidget/imgs/work-icon.png");
const currentLocationIcon = require("../../../components/DeliveryPickUpWidget/imgs/current-icon.png");
const storePickupIcon = require("../../../components/DeliveryPickUpWidget/icons/store-pickup.svg");
const infoIcon = require("../../../components/LocationsListItem/imgs/info-icon.png");
const deliveryIcon = require("../../../components/LocationsListItem/imgs/delivery.svg");

const PickupLocationSearch = ({ onSelect, onClose }) => {
  const inputRef = useRef(null);
  const searchStores = useSearchStores();
  const searchAddresses = useAddressSearch();
  const savedLocations = useSavedLocations();
  const userAddress = useUserAddress();
  const [state, setState] = useReducer(
    (currentState, partialState) => ({
      ...currentState,
      ...partialState
    }),
    {
      value: ""
    }
  );

  const handleUpdateSearchValue = e => {
    setState({ value: e.target.value });
    searchAddresses.fetch(e.target.value);
  };

  useEffect(() => {
    if (!savedLocations.fetched) {
      savedLocations.fetch();
    }
  }, [savedLocations.fetched, savedLocations.fetch]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-center relative">
        <div className="border-solid bg-white border-lightDry border-[1px] items-center px-3 py-1 rounded-full flex gap-2">
          <img
            alt=""
            src={storePickupIcon}
            className={`${styles.searchIcon} h-[16px] w-[16px]`}
          />
          <span className="font-montserratBold text-sm text-dark">Pickup</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          tabIndex={0}
          onKeyDown={e => e.key === "Enter" && onClose()}
          className="absolute top-0 right-0 bg-transparent p-0 m-0 w-auto h-auto font-sans"
        >
          X
        </button>
      </div>
      <div className="border-solid bg-white border-lightDry border-[1px] w-full flex items-center p-2 rounded-full relative">
        <img alt="" src={searchIcon} className={styles.searchIcon} />
        <input
          ref={inputRef}
          value={state.searchValue}
          placeholder="Find bakery near you"
          onChange={handleUpdateSearchValue}
          className="font-montserratMedium bg-transparent outline-none text-dark w-full px-2"
        />
        {/* Loader removed - using global loader instead */}
      </div>
      {!!userAddress && (
        <button
          type="button"
          data-testid="current-location-button"
          className={`${styles.listItem} flex items-center gap-3 px-2 py-2 w-full`}
          onClick={() => {
            searchStores.fetch({
              lat: userAddress.lat,
              lng: userAddress.lng
            });
          }}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === "Enter") {
              searchStores.fetch({
                lat: userAddress.lat,
                lng: userAddress.lng
              });
            }
          }}
        >
          <img
            src={currentLocationIcon}
            alt=""
            className={styles.locationIcon}
          />
          <div className="flex flex-col gap-1 text-left">
            <span className="text-darkElevationPrimary text-xs">
              Use my current location
            </span>
            <span className="font-montserratBold text-dark text-sm">
              {`${userAddress.address1}, ${userAddress.city}, ${userAddress.state} ${userAddress.postcode}`}
            </span>
          </div>
        </button>
      )}
      {searchAddresses.showCaptchaChallenge && (
        <div className="text-center py-4">
          <ReCAPTCHAForm setToken={searchAddresses.onCaptchaVerify} />
        </div>
      )}
      {!searchAddresses.showCaptchaChallenge &&
        !!searchAddresses?.locations?.length && (
          <ul className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
            {searchAddresses.locations.slice(0, 3).map(location => {
              return (
                <li key={location.externalId}>
                  <button
                    type="button"
                    className={`${styles.listItem} flex gap-4 items-center px-2 py-2 w-full h-auto text-left`}
                    onClick={() =>
                      searchStores.fetch({
                        externalId: location.externalId,
                        orderTypeId: orderTypeIds.pickup,
                        lat: null,
                        lng: null
                      })}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        searchStores.fetch({
                          externalId: location.externalId,
                          orderTypeId: orderTypeIds.pickup,
                          lat: null,
                          lng: null
                        });
                      }
                    }}
                  >
                    <img
                      src={locationIcon}
                      alt=""
                      className={styles.locationIcon}
                    />
                    <span className="font-montserratBold text-dark text-sm">
                      {location.address1}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      <div className="border-0 border-b-2 border-dotted border-dark" />
      {searchStores.showCaptchaChallenge && (
        <div className="text-center py-4">
          <ReCAPTCHAForm setToken={searchStores.onCaptchaVerify} />
        </div>
      )}
      {!searchStores.showCaptchaChallenge &&
      (searchStores.loading || searchStores.fetched) ? (
        <>
          <p className="font-montserratBold">Closest Locations</p>
          <ul className="flex flex-col gap-2">
            {searchStores.stores?.map(store => {
              return (
                <li key={store.id}>
                  <LocationItem onSelect={onSelect} store={store} />
                </li>
              );
            })}
            {/* Loader removed - using global loader instead */}
          </ul>
        </>
      ) : (
        !searchStores.showCaptchaChallenge && (
          <div className="flex flex-col gap-2">
            <p className="font-montserratBold">Favorite Bakeries</p>
            <ul>
              {savedLocations.locations?.map(location => {
                return (
                  <li key={location.id}>
                    <LocationItem onSelect={onSelect} store={location} />
                  </li>
                );
              })}
            </ul>
            {/* Loader removed - using global loader instead */}
            {savedLocations.fetched &&
              savedLocations.locations?.length === 0 && (
                <p className="text-center text-dark font-montserratMedium">
                  No favorite bakeries saved
                </p>
              )}
          </div>
        )
      )}
    </div>
  );
};

PickupLocationSearch.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

const LocationItem = ({ onSelect, store }) => {
  const phonePrefixes = useSelector(state => state.country.phonePrefixes);
  return (
    <button
      tabIndex={0}
      type="button"
      onClick={() => onSelect(store)}
      onKeyDown={e => e.key === "Enter" && onSelect(store)}
      data-testid={`location-item-${store.id}`}
      className={`${styles.listItem} flex gap-4 px-2 py-2 w-full`}
    >
      <div className="flex flex-col gap-1.5 text-left flex-1">
        <div className="flex gap-2 items-center text-dark dark:text-white font-montserratMedium  text-[14px]">
          <span className="font-montserratBold text-dark text-sm">
            {store.name}
          </span>
          <img alt="" src={infoIcon} className="h-[18px] w-[18px]" />
          <div className="has-tooltip">
            <span
              id={`delivery-tooltip-${store.id}`}
              className="tooltip rounded shadow-lg p-1 bg-[#ce4396] text-white mt-6 px-2 py-1"
              role="tooltip"
              aria-hidden="true"
            >
              This address is in range of delivery for this store.
            </span>
            <button
              type="button"
              aria-labelledby={`delivery-tooltip-${store.id}`}
              className="inline-flex items-center bg-transparent border-none p-0 cursor-pointer w-8 h-8 has-tooltip-button"
              onFocus={e => {
                const tooltipContainer = e.currentTarget.closest(
                  ".has-tooltip"
                );
                const tooltip = tooltipContainer?.querySelector(
                  `#delivery-tooltip-${store.id}`
                );
                if (tooltip) {
                  tooltip.setAttribute("aria-hidden", "false");
                  tooltip.classList.add("tooltip-visible");
                }
              }}
              onBlur={e => {
                // Don't hide on blur if focus is moving to the tooltip itself
                const { relatedTarget } = e;
                const tooltipContainer = e.currentTarget.closest(
                  ".has-tooltip"
                );
                const tooltip = tooltipContainer?.querySelector(
                  `#delivery-tooltip-${store.id}`
                );
                if (tooltip && !tooltip.contains(relatedTarget)) {
                  tooltip.setAttribute("aria-hidden", "true");
                  tooltip.classList.remove("tooltip-visible");
                }
              }}
              onKeyDown={e => {
                const tooltipContainer = e.currentTarget.closest(
                  ".has-tooltip"
                );
                const tooltip = tooltipContainer?.querySelector(
                  `#delivery-tooltip-${store.id}`
                );

                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (tooltip) {
                    const isHidden =
                      tooltip.getAttribute("aria-hidden") === "true";
                    tooltip.setAttribute(
                      "aria-hidden",
                      isHidden ? "false" : "true"
                    );
                    if (isHidden) {
                      tooltip.classList.add("tooltip-visible");
                    } else {
                      tooltip.classList.remove("tooltip-visible");
                    }
                  }
                } else if (e.key === "Escape" || e.key === "Esc") {
                  e.preventDefault();
                  if (tooltip) {
                    tooltip.setAttribute("aria-hidden", "true");
                    tooltip.classList.remove("tooltip-visible");
                    // Return focus to button
                    e.currentTarget.focus();
                  }
                }
              }}
            >
              <img
                src={deliveryIcon}
                alt="Delivery available - This address is in range of delivery for this store"
              />
            </button>
          </div>
        </div>

        {store.note ? (
          <p className="text-darkElevationPrimary dark:text-darkElevationSecondary">
            {store.note}
          </p>
        ) : null}

        <div className="text-darkElevationPrimary dark:text-darkElevationSecondary flex flex-col gap-2 text-[14px]">
          <p className="truncate overflow-ellipsis overflow-hidden w-[250px]">
            {store.mailingAddress}
            <br />
            {`${store.city}, ${store.state}, ${store.zip}`}
          </p>
          <p>
            <Link to="/contact">
              {phoneService.applyMask(store.phone, phonePrefixes)}
            </Link>
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 text-right pt-1">
        <div className="flex items-center font-montserratMedium  text-[14px]">
          {store.storeType === storeTypes.COMINGSOON ? (
            <p className="text-[#ce4396]">
              <span>Coming Soon </span>
              {`${store.distanceToStore !== "" ? "|" : ""}`}
              {` ${store.distanceToStore}`}
            </p>
          ) : (
            <p className={!store.isPickupOpen ? "text-[#ce4396]" : undefined}>
              <span>{`${store.isPickupOpen ? "Open " : "Closed "}`}</span>
              {`${store.distanceToStore !== "" ? "|" : ""}`}
              {` ${store.distanceToStore}`}
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

LocationItem.propTypes = {
  onSelect: PropTypes.func.isRequired,
  store: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    address: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isDeliveryInRange: PropTypes.bool.isRequired,
    isPickupOpen: PropTypes.bool.isRequired,
    storeType: PropTypes.string.isRequired,
    distanceToStore: PropTypes.string.isRequired,
    note: PropTypes.string,
    mailingAddress: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired
  }).isRequired
};

export default memo(PickupLocationSearch);
